import React, { useEffect, useState } from "react";
import Card from "components/card";
import { fetchOrgTree, setLoginPassword } from "utils/orgApi";
import DeviceTab from "./deviceTab";
import { readUser } from "utils/session";

function Detail({ label, value }) {
  return (
    <p className="text-sm text-gray-600 dark:text-gray-300">
      <span className="font-semibold text-navy-700 dark:text-white">{label}: </span>
      {value || "—"}
    </p>
  );
}

function LoginPassword({ userId }) {
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setMessage("");
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await setLoginPassword({ userId, password });
      setPassword("");
      setMessage("Password saved");
    } catch (err) {
      setError(err.message || "The password was not saved.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <input
        type="password"
        autoComplete="new-password"
        placeholder="New password"
        className="rounded-md border border-gray-200 bg-white px-3 py-1 text-sm text-navy-700 dark:border-white/10 dark:bg-navy-900 dark:text-white"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
      />
      <button
        type="button"
        disabled={busy}
        className="rounded-md border border-gray-300 px-3 py-1 text-sm font-semibold text-navy-700 disabled:opacity-50 dark:border-white/10 dark:text-white"
        onClick={save}
      >
        {busy ? "Saving…" : "Save password"}
      </button>
      {message && <span className="text-sm text-green-600">{message}</span>}
      {error && <span className="text-sm text-red-500">{error}</span>}
    </div>
  );
}

function Contacts({ people }) {
  if (!people.length) {
    return <p className="text-sm text-gray-500">No contact personnel</p>;
  }
  return (
    <ul className="mt-1 space-y-3">
      {people.map((person) => (
        <li key={person.userId || person.username} className="text-sm text-gray-600 dark:text-gray-300">
          {person.displayName}
          {person.email ? ` · ${person.email}` : ""}
          {person.phone ? ` · ${person.phone}` : ""}
          {person.userId ? <LoginPassword userId={person.userId} /> : null}
        </li>
      ))}
    </ul>
  );
}

function Devices({ devices }) {
  if (!devices.length) {
    return <p className="text-sm text-gray-500">No devices recorded</p>;
  }
  return (
    <ul className="mt-1 space-y-1">
      {devices.map((device) => (
        <li key={device.did} className="text-sm text-navy-700 dark:text-white">
          {device.aid || "No asset id"} <span className="text-gray-500">DID {device.did}</span>
        </li>
      ))}
    </ul>
  );
}

function UserTree({ refreshKey }) {
  const [tree, setTree] = useState(null);
  const [error, setError] = useState("");
  const [openZones, setOpenZones] = useState({});
  const [openBranches, setOpenBranches] = useState({});

  useEffect(() => {
    let cancelled = false;
    fetchOrgTree()
      .then((data) => {
        if (cancelled) return;
        setTree(data);
        const first = data.zones?.[0];
        if (first) setOpenZones((current) => (Object.keys(current).length ? current : { [first.zoneId]: true }));
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Could not load the organization.");
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const toggle = (setter, id) => {
    setter((current) => ({ ...current, [id]: !current[id] }));
  };

  return (
    <>
        {error && <p className="mt-4 text-sm text-red-500">{error}</p>}
        {!tree && !error && <p className="mt-4 text-sm text-gray-500">Loading organization…</p>}
        {tree && (
          <div className="mt-6 space-y-3">
            {tree.zones.map((zone) => {
              const zoneOpen = !!openZones[zone.zoneId];
              const deviceCount =
                zone.unassignedDevices.length +
                zone.branches.reduce((sum, branch) => sum + branch.devices.length, 0);
              return (
                <div key={zone.zoneId} className="rounded-md border border-gray-200 dark:border-white/10">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between px-4 py-3 text-left"
                    onClick={() => toggle(setOpenZones, zone.zoneId)}
                  >
                    <span className="font-bold text-navy-700 dark:text-white">{zone.name}</span>
                    <span className="text-sm text-gray-500">
                      {zone.branches.length} branches · {deviceCount} devices {zoneOpen ? "−" : "+"}
                    </span>
                  </button>
                  {zoneOpen && (
                    <div className="space-y-3 border-t border-gray-200 px-4 py-3 dark:border-white/10">
                      <Detail label="Address" value={[zone.address, zone.city, zone.state, zone.pincode].filter(Boolean).join(", ")} />
                      <Detail label="Phone" value={zone.phone} />
                      <Detail label="Email" value={zone.email} />
                      <div>
                        <p className="text-sm font-semibold text-navy-700 dark:text-white">Zone contacts</p>
                        <Contacts people={zone.contacts} />
                      </div>
                      {zone.branches.map((branch) => {
                        const branchOpen = !!openBranches[branch.branchId];
                        return (
                          <div key={branch.branchId} className="ml-4 rounded-md bg-lightPrimary p-3 dark:bg-navy-900">
                            <button
                              type="button"
                              className="flex w-full items-center justify-between text-left"
                              onClick={() => toggle(setOpenBranches, branch.branchId)}
                            >
                              <span className="font-semibold text-navy-700 dark:text-white">{branch.name}</span>
                              <span className="text-sm text-gray-500">
                                {branch.devices.length} devices {branchOpen ? "−" : "+"}
                              </span>
                            </button>
                            {branchOpen && (
                              <div className="mt-2 space-y-2">
                                <Detail label="Branch code" value={branch.branchCode} />
                                <Detail label="Address" value={[branch.address, branch.city, branch.state, branch.pincode].filter(Boolean).join(", ")} />
                                <Detail label="Phone" value={branch.phone} />
                                <Detail label="Email" value={branch.email} />
                                <div>
                                  <p className="text-sm font-semibold text-navy-700 dark:text-white">Branch contacts</p>
                                  <Contacts people={branch.contacts} />
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-navy-700 dark:text-white">Devices</p>
                                  <Devices devices={branch.devices} />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {zone.unassignedDevices.length > 0 && (
                        <div className="ml-4">
                          <p className="text-sm font-semibold text-navy-700 dark:text-white">Devices not in a branch</p>
                          <Devices devices={zone.unassignedDevices} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
    </>
  );
}

export default function UserDeviceManagement() {
  const [tab, setTab] = useState("user");
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div className="mt-3">
      <Card extra="p-6">
        <h2 className="text-2xl font-bold text-navy-700 dark:text-white">User & Device control</h2>
        <p className="mt-1 text-sm text-gray-600">
          {readUser()?.customerName || "This account"}
        </p>
        <div className="mt-4 flex gap-2 border-b border-gray-200 dark:border-white/10">
          {[
            ["user", "User"],
            ["device", "Device"],
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={`px-4 py-2 text-sm font-semibold ${
                tab === id
                  ? "border-b-2 border-brand-500 text-navy-700 dark:text-white"
                  : "text-gray-500"
              }`}
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
        </div>
        {tab === "user" ? (
          <UserTree refreshKey={refreshKey} />
        ) : (
          <DeviceTab onChanged={() => setRefreshKey((value) => value + 1)} />
        )}
      </Card>
    </div>
  );
}
